import { useEffect, useMemo, useState } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { getStorageJSON } from "../../utils/helpers";
import { db } from "../../firebase/firestore";
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { logError, logSuccess, getErrorMessage } from "../../utils/errorHandler";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const user = getStorageJSON("user", null);
      setLoading(true);
      setError("");

      if (user?.uid) {
        logSuccess("Notifications", "Fetching from Firebase...");
        const q = query(collection(db, "notifications"), where("userId", "==", user.uid));
        const unsubscribe = onSnapshot(
          q,
          (snap) => {
            try {
              const all = snap.docs
                .map((d) => ({ firestoreId: d.id, ...d.data() }))
                .sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0));
              setNotifications(all);
              setLoading(false);
              setError("");
              logSuccess("Notifications", `Loaded ${all.length} notifications`);
            } catch (err) {
              logError("Notifications Snapshot", err);
              setError(getErrorMessage(err));
              setLoading(false);
            }
          },
          (error) => {
            logError("Notifications Listener", error);
            setError(getErrorMessage(error));
            const fallback = getStorageJSON("customerNotifications", []);
            setNotifications(Array.isArray(fallback) ? fallback : []);
            setLoading(false);
          }
        );
        return () => unsubscribe();
      }

      const loadNotifications = () => {
        const data = getStorageJSON("customerNotifications", []);
        setNotifications(Array.isArray(data) ? data : []);
        setLoading(false);
        setError("");
      };

      loadNotifications();
      window.addEventListener("storage", loadNotifications);
      window.addEventListener("notifications-updated", loadNotifications);

      return () => {
        window.removeEventListener("storage", loadNotifications);
        window.removeEventListener("notifications-updated", loadNotifications);
      };
    } catch (err) {
      logError("Notifications Setup", err);
      setError(getErrorMessage(err));
      setLoading(false);
    }
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const markAllRead = () => {
    try {
      const user = getStorageJSON("user", null);

    if (user?.uid) {
      Promise.all(
        notifications
          .filter((n) => n.firestoreId && !n.read)
          .map((n) => updateDoc(doc(db, "notifications", n.firestoreId), { read: true }))
      ).catch((err) => {
        logError("Mark All Read", err);
      });
      return;
    }

    const nextData = notifications.map((n) => ({ ...n, read: true }));
    setNotifications(nextData);
    localStorage.setItem("customerNotifications", JSON.stringify(nextData));
    window.dispatchEvent(new Event("notifications-updated"));
    logSuccess("Notifications", "Marked all as read");
    } catch (err) {
      logError("Mark All Read", err);
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="home">
      <Navbar />

      <div className="customer-page">

        {/* 🔥 ERROR DISPLAY */}
        {error && (
          <div style={{ margin: "20px", padding: "12px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(220, 38, 38, 0.3)", borderRadius: "8px", color: "#7f1d1d", borderLeft: "4px solid #ef4444" }}>
            ⚠️ {error}
          </div>
        )}

        {/* 🔥 LOADING STATE */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", fontSize: "1.1rem", color: "#666" }}>
            ⏳ Loading notifications...
          </div>
        ) : (
          <>
        <div className="customer-page-header">
          <h2>Notifications</h2>
          <p>{unreadCount} unread updates</p>
        </div>

        <div className="page-actions">
          <button className="secondary-action" onClick={markAllRead}>Mark all as read</button>
        </div>

        {!notifications.length ? (
          <div className="empty-state">
            <h3>No notifications</h3>
            <p>Important order and support updates will show up here.</p>
          </div>
        ) : (
          <div className="customer-grid">
            {notifications.map((note) => (
              <article className="customer-card" key={note.id}>
                <div className="row-between">
                  <h3>{note.title || "Update"}</h3>
                  {!note.read && <span className="status-chip">New</span>}
                </div>
                <p>{note.message}</p>
                <small>{new Date(note.createdAt).toLocaleString()}</small>
              </article>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      <Footer />
    </div>
  );
}
